/**
 * Learning Suggestions Panel
 * 
 * Shows AI recommendations based on learned rules and patterns
 * Appears when user is correcting transactions to suggest similar patterns
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { LearningRulesService } from '@/services/learningRulesService';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { 
  Brain, 
  Lightbulb, 
  TrendingUp, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Sparkles
} from 'lucide-react';
import type { LearnedRule } from '@/types/learningRules';

interface LearningSuggestionsPanelProps {
  transactionData?: {
    counterparty?: string;
    description?: string;
    amount?: number;
  };
  onApplySuggestion?: (rule: LearnedRule) => void;
  onLearnFromCorrection?: (correction: any) => void;
  className?: string;
}

interface Suggestion {
  rule: LearnedRule;
  confidence: number;
  reasoning: string;
  matchType: 'counterparty' | 'description' | 'amount_range' | 'pattern';
}

export const LearningSuggestionsPanel = ({
  transactionData,
  onApplySuggestion,
  onLearnFromCorrection,
  className = ''
}: LearningSuggestionsPanelProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [applyingRule, setApplyingRule] = useState<string | null>(null);

  // Load suggestions when transaction data changes
  useEffect(() => {
    if (transactionData && user) {
      loadSuggestions();
    }
  }, [transactionData, user]);

  const loadSuggestions = async () => {
    if (!transactionData || !user) return;

    setLoading(true);
    try {
      // Get all active rules for the user
      const rules = await LearningRulesService.getRules({ status: 'active' });
      
      // Find matching rules based on transaction data
      const matches: Suggestion[] = [];
      
      rules.forEach(rule => {
        let confidence = 0;
        let reasoning = '';
        let matchType: Suggestion['matchType'] = 'pattern';

        // Check counterparty match
        if (transactionData.counterparty && rule.pattern_type === 'counterparty') {
          const counterparty = transactionData.counterparty.toLowerCase();
          const pattern = rule.pattern_key.toLowerCase();
          
          if (counterparty.includes(pattern) || pattern.includes(counterparty)) {
            confidence = rule.current_confidence * 100;
            reasoning = `Counterparty "${transactionData.counterparty}" matches pattern "${rule.pattern_key}"`;
            matchType = 'counterparty';
          }
        }

        // Check description match
        if (transactionData.description && rule.pattern_type === 'description_pattern') {
          const description = transactionData.description.toLowerCase();
          const pattern = rule.pattern_key.toLowerCase();
          
          if (description.includes(pattern) || pattern.includes(description)) {
            confidence = rule.current_confidence * 100;
            reasoning = `Description contains pattern "${rule.pattern_key}"`;
            matchType = 'description';
          }
        }

        // Check amount range match
        if (transactionData.amount && rule.pattern_type === 'amount_based' && rule.pattern_value) {
          const amount = Math.abs(transactionData.amount);
          const [min, max] = rule.pattern_value.split('-').map(Number);
          
          if (amount >= min && amount <= max) {
            confidence = rule.current_confidence * 100;
            reasoning = `Amount ${amount} falls within range ${rule.pattern_value}`;
            matchType = 'amount_range';
          }
        }

        // Add to suggestions if confidence is high enough
        if (confidence >= 70) {
          matches.push({
            rule,
            confidence,
            reasoning,
            matchType
          });
        }
      });

      // Sort by confidence (highest first)
      matches.sort((a, b) => b.confidence - a.confidence);
      
      setSuggestions(matches.slice(0, 3)); // Show top 3 suggestions
    } catch (error) {
      console.error('Error loading suggestions:', error);
      toast({
        title: 'Fehler',
        description: 'KI-Vorschläge konnten nicht geladen werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApplySuggestion = async (suggestion: Suggestion) => {
    setApplyingRule(suggestion.rule.id);
    try {
      await onApplySuggestion?.(suggestion.rule);
      
      toast({
        title: 'Regel angewendet',
        description: `"${suggestion.rule.pattern_key}" wurde erfolgreich angewendet.`,
      });
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Regel konnte nicht angewendet werden.',
        variant: 'destructive',
      });
    } finally {
      setApplyingRule(null);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600 bg-green-50';
    if (confidence >= 80) return 'text-blue-600 bg-blue-50';
    if (confidence >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-orange-600 bg-orange-50';
  };

  const getMatchTypeIcon = (matchType: Suggestion['matchType']) => {
    switch (matchType) {
      case 'counterparty': return '👤';
      case 'description': return '📝';
      case 'amount_range': return '💰';
      default: return '🔍';
    }
  };

  if (!transactionData) {
    return (
      <Card className={`border-2 border-dashed border-gray-200 ${className}`}>
        <CardContent className="p-6 text-center">
          <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">
            KI-Lernvorschläge
          </h3>
          <p className="text-sm text-gray-500">
            Wählen Sie eine Transaktion aus, um KI-basierte Vorschläge zu erhalten
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50 ${className}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
            <Brain className="h-4 w-4 text-white" />
          </div>
          KI-Lernvorschläge
          <Badge variant="secondary" className="ml-auto">
            {suggestions.length} Vorschläge
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-6">
            <Lightbulb className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-600 mb-2">
              Keine passenden Regeln gefunden
            </p>
            <p className="text-xs text-gray-500">
              Korrigieren Sie diese Transaktion, um eine neue Regel zu lernen
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.rule.id}
                className="bg-white rounded-lg p-4 border border-gray-200 hover:border-purple-300 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getMatchTypeIcon(suggestion.matchType)}</span>
                    <div>
                      <h4 className="font-medium text-sm text-gray-900">
                        {suggestion.rule.pattern_key}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {suggestion.reasoning}
                      </p>
                    </div>
                  </div>
                  <Badge 
                    className={`text-xs font-medium ${getConfidenceColor(suggestion.confidence)}`}
                  >
                    {suggestion.confidence.toFixed(0)}%
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    <div className="font-mono">
                      Soll: {suggestion.rule.suggested_debit_account} / 
                      Haben: {suggestion.rule.suggested_credit_account}
                    </div>
                    {suggestion.rule.description_template && (
                      <div className="mt-1">
                        "{suggestion.rule.description_template}"
                      </div>
                    )}
                  </div>
                  
                  <Button
                    size="sm"
                    onClick={() => handleApplySuggestion(suggestion)}
                    disabled={applyingRule === suggestion.rule.id}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {applyingRule === suggestion.rule.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Anwenden
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Learning Stats */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              <span>Lernfortschritt</span>
            </div>
            <div className="flex items-center gap-2">
              <span>Regeln gelernt diese Woche: 0</span>
              <Sparkles className="h-3 w-3 text-purple-500" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
